<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    onBeforeUnmount,
    ref,
    watch,
  } from 'vue';
  import { useOnline } from '@vueuse/core';
  import PosUiField from '@/components/pos-v2/ui/PosUiField.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import V2SetupBusinessTypes from '@/components/v2/store-setup/business/V2SetupBusinessTypes.vue';
  import V2SetupCurrencyField from '@/components/v2/store-setup/business/V2SetupCurrencyField.vue';
  import V2SetupHoursField from '@/components/v2/store-setup/business/V2SetupHoursField.vue';
  import V2SetupLogoTile from '@/components/v2/store-setup/business/V2SetupLogoTile.vue';
  import V2SetupSlugField from '@/components/v2/store-setup/business/V2SetupSlugField.vue';
  import V2SetupStorePreview from '@/components/v2/store-setup/shared/V2SetupStorePreview.vue';
  import V2StoreMonogram from '@/components/v2/store-setup/shared/V2StoreMonogram.vue';
  import { flowBar } from '@/composition/store-setup/setup-flow-screen';
  import type { IFlowBarState } from '@/composition/store-setup/setup-flow-screen';
  import type { TSlugState } from '@/composition/store-setup/use-slug-availability';
  import type {
    IStoreSetupDraft,
    TStoreSetupDraftPatch,
  } from '@/composition/store-setup/use-store-setup-draft';
  import {
    NAME_MAX,
    canCreateStore,
    draftCurrency,
    draftSlug,
    isValidStoreName,
  } from '@/domain/store-setup/business-step';
  import type { ISlugConflict } from '@/domain/store-setup/business-step';
  import { todayHoursLabel } from '@/domain/store-setup/opening-hours';
  import type { TWeekHours } from '@/domain/store-setup/opening-hours';
  import { storeInitials } from '@/domain/store-setup/store-identity';
  import { defaultBrand } from '@/domain/storefront/storefront-colors';
  import type { TBusinessType } from '@/interfaces/IStoreSetup';
  import { Globals } from '@/modules/Globals';
  import { nextDomId } from '@/utils/dom-id.utils';

  /**
   * Step 2 · «Negocio» (plan §4.2): name, link, business type, hours,
   * currency and logo, with a live preview of the store page. Everything is
   * patched into the local draft; «Crear mi tienda» asks the page to create
   * the store (`submit`). Exposes the screen handle of the page's bottom bar.
   */
  const MINI_LOGO_SIZE = 40;

  const props = withDefaults(
    defineProps<{
      draft: IStoreSetupDraft;
      wide: boolean;
      /** The create call is running: nothing can change. */
      busy: boolean;
      /** The link the create call refused twice (shown taken with the links it offered). */
      slugConflict?: ISlugConflict | null;
    }>(),
    { slugConflict: null }
  );
  const emit = defineEmits<{
    (e: 'patch', patch: TStoreSetupDraftPatch): void;
    (e: 'uploading', uploading: boolean): void;
    (e: 'submit'): void;
    (e: 'back'): void;
  }>();
  const proxy = getCurrentInstance()!.proxy;
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const online = useOnline();

  const nameId = nextDomId('setup-business-name');
  const nameTouched = ref(false);
  const slugState = ref<TSlugState>('idle');
  const logoUploading = ref(false);
  /** The narrow preview's logo failed to load: the initials stand in. */
  const miniLogoFailed = ref(false);

  //#region Derived
  const city = computed(() => props.draft.location?.city ?? null);
  const slug = computed(() => draftSlug(props.draft));
  const currency = computed(() => draftCurrency(props.draft));
  const initials = computed(() => storeInitials(props.draft.name));
  const displayName = computed(
    () => props.draft.name.trim() || tr('storeSetup.preview.placeholderName')
  );
  const address = computed(() => props.draft.location?.address ?? '');
  const logoUrl = computed(() =>
    props.draft.logoPath
      ? `${Globals.uploadsPath}/${props.draft.logoPath}`
      : null
  );
  const todayHours = computed(() =>
    props.draft.hours ? todayHoursLabel(props.draft.hours, new Date()) : null
  );
  /** The preview's colour: the logo's, else a food hue from the name (D-ADM-5). */
  const brand = computed(() =>
    defaultBrand({ logoColor: props.draft.logoColor, name: props.draft.name })
  );
  const nameError = computed(() =>
    nameTouched.value && !isValidStoreName(props.draft.name)
      ? tr('storeSetup.business.nameError')
      : undefined
  );
  const ready = computed(() =>
    canCreateStore({
      name: props.draft.name,
      slug: slug.value,
      slugState: slugState.value,
      currency: currency.value,
      logoUploading: logoUploading.value,
    })
  );
  const primaryLabel = computed(() => {
    if (props.busy) return tr('storeSetup.business.creating');
    if (logoUploading.value) return tr('storeSetup.business.logoUploading');
    return tr('storeSetup.business.create');
  });
  const bar = computed<IFlowBarState>(() =>
    flowBar({
      backLabel: tr('storeSetup.back'),
      helper: tr('storeSetup.business.helperNext'),
      primaryLabel: primaryLabel.value,
      primaryDisabled: !ready.value || !online.value,
      primaryBusy: props.busy,
    })
  );
  //#endregion

  //#region Fields
  function onName(value: string) {
    emit('patch', { name: value });
  }

  function onSlug(value: string) {
    if (value !== props.draft.slug) emit('patch', { slug: value });
  }

  function onSlugEdited() {
    emit('patch', { slugEdited: true });
  }

  function onType(code: TBusinessType) {
    // A type the owner picked is no longer Google's.
    emit('patch', { businessType: code, businessTypeFromGoogle: false });
  }

  function onHours(week: TWeekHours | null) {
    // Hours set in the editor are the owner's own, confirmed by «Listo».
    emit('patch', {
      hours: week,
      hoursFromGoogle: false,
      hoursConfirmed: week !== null,
    });
  }

  function onHoursConfirm() {
    emit('patch', { hoursConfirmed: true });
  }

  function onCurrency(code: string) {
    emit('patch', { currency: code });
  }

  function onLogo(path: string | null) {
    emit('patch', { logoPath: path });
  }

  function onLogoColor(color: string | null) {
    emit('patch', { logoColor: color });
  }

  function onUploading(value: boolean) {
    logoUploading.value = value;
    emit('uploading', value);
  }
  //#endregion

  //#region Screen handle
  async function primary() {
    nameTouched.value = true;
    if (props.busy || !ready.value || !online.value) return;
    emit('submit');
  }

  async function back() {
    if (!props.busy) emit('back');
  }

  function nothing(): Promise<void> {
    return Promise.resolve();
  }

  defineExpose({ bar, primary, secondary: nothing, back });
  //#endregion

  watch(
    () => props.draft.logoPath,
    () => {
      miniLogoFailed.value = false;
    }
  );

  onBeforeUnmount(() => {
    // The page must not wait for a logo upload this screen will not finish.
    if (logoUploading.value) emit('uploading', false);
  });
</script>

<template>
  <div class="v2-setup-step-business" :class="wide ? 'is-wide' : 'is-narrow'">
    <section class="business-card">
      <header class="step-head">
        <h1 class="step-title" tabindex="-1">
          {{ $t('storeSetup.business.title') }}
        </h1>
        <p class="step-subtitle">{{ $t('storeSetup.business.subtitle') }}</p>
      </header>
      <div v-if="!wide" class="mini-preview" aria-hidden="true">
        <img
          v-if="logoUrl && !miniLogoFailed"
          class="mini-logo"
          :src="logoUrl"
          alt=""
          @error="miniLogoFailed = true"
        />
        <V2StoreMonogram v-else :initials="initials" :size="MINI_LOGO_SIZE" />
        <div class="mini-text">
          <p class="mini-name">{{ displayName }}</p>
          <p class="mini-caption">{{ $t('storeSetup.preview.caption') }}</p>
        </div>
      </div>
      <PosUiField
        :label="$t('storeSetup.business.name')"
        :hint="$t('storeSetup.business.nameHint')"
        :error="nameError"
        :input-id="nameId"
      >
        <PosUiInput
          :id="nameId"
          :value="draft.name"
          :maxlength="NAME_MAX"
          autocomplete="organization"
          :disabled="busy"
          @input="onName"
          @blur="nameTouched = true"
        />
      </PosUiField>
      <V2SetupSlugField
        :name="draft.name"
        :city="city"
        :value="slug"
        :edited="draft.slugEdited"
        :disabled="busy"
        :conflict="slugConflict"
        @input="onSlug"
        @edited="onSlugEdited"
        @state="slugState = $event"
      />
      <V2SetupBusinessTypes
        :value="draft.businessType"
        :from-google="draft.businessTypeFromGoogle"
        :disabled="busy"
        @input="onType"
      />
      <V2SetupHoursField
        :value="draft.hours"
        :from-google="draft.hoursFromGoogle"
        :confirmed="draft.hoursConfirmed"
        :disabled="busy"
        @input="onHours"
        @confirm="onHoursConfirm"
      />
      <V2SetupCurrencyField
        :country="draft.country"
        :value="currency"
        :disabled="busy"
        @input="onCurrency"
      />
      <V2SetupLogoTile
        :value="draft.logoPath"
        :initials="initials"
        :disabled="busy"
        @input="onLogo"
        @logo-color="onLogoColor"
        @uploading="onUploading"
      />
    </section>
    <aside v-if="wide" class="business-preview">
      <V2SetupStorePreview
        variant="store"
        :brand="brand"
        :name="draft.name"
        :address="address"
        :logo-url="logoUrl"
        :initials="initials"
        :today-hours="todayHours"
      />
    </aside>
  </div>
</template>

<style scoped lang="scss">
  .v2-setup-step-business {
    @apply tw-mx-auto tw-w-full;
    &.is-narrow {
      @apply tw-max-w-2xl;
      .business-card {
        @apply tw-p-4;
      }
    }
    // Wide: the form beside a 22 rem sticky preview.
    &.is-wide {
      @apply tw-grid tw-max-w-5xl tw-items-start tw-gap-6;
      grid-template-columns: minmax(0, 1fr) 22rem;
      .business-card {
        @apply tw-p-6;
      }
    }
    .business-card {
      @apply tw-flex tw-min-w-0 tw-flex-col tw-gap-5 tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-white;
    }
    .step-head {
      @apply tw-flex tw-flex-col tw-gap-1;
    }
    .step-title {
      @apply tw-m-0 tw-text-xl tw-font-bold tw-text-neutral-900 md:tw-text-2xl;
    }
    .step-subtitle {
      @apply tw-m-0 tw-text-sm tw-text-neutral-500 md:tw-text-base;
    }
    .mini-preview {
      @apply tw-flex tw-items-center tw-gap-3 tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-px-3 tw-py-2.5;
    }
    .mini-logo {
      @apply tw-h-10 tw-w-10 tw-shrink-0 tw-rounded-md tw-object-cover;
    }
    .mini-text {
      @apply tw-flex tw-min-w-0 tw-flex-col;
    }
    .mini-name {
      @apply tw-m-0 tw-truncate tw-text-sm tw-font-medium tw-text-neutral-900;
    }
    .mini-caption {
      @apply tw-m-0 tw-text-xs tw-text-neutral-500;
    }
    .business-preview {
      @apply tw-sticky tw-top-0 tw-min-w-0;
    }
  }
</style>
